'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  InputAdornment,
  Paper,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LineChart as LineChartIcon } from 'lucide-react';

import { addDays, formatIsoDate, parseIsoDate } from '@/libs/fixed-income/business-calendar';
import {
  isRealRateIndexed,
  simulateTreasuryMarkToMarket,
  type SimulationResult,
  type SimulationSeriesPoint,
} from '@/libs/fixed-income/treasury-simulation';

type CatalogOption = {
  id: string;
  idCem: string;
  name: string;
  titleType: string;
  indexer: string;
  conversionDate: string | null;
  maturityDate: string | null;
};

type SimulationPayload = {
  instrument: {
    idCem: string;
    name: string;
    titleType: string;
    indexer: string;
    cashFlowType: string;
    riskFactor: string;
    taxRegime: string;
    maturityDate: string | null;
    conversionDate: string | null;
    quoteDate: string | null;
    sellRateMorning: number | null;
    sellPuMorning: number | null;
  };
  quotes: {
    date: string;
    buyRate: number | null;
    sellRate: number | null;
    buyPu: number | null;
    sellPu: number | null;
  }[];
  flows: { paymentDate: string; flowType: string; amount: number | null; indexer: string | null }[];
  custodyFeeRate: number;
  benchmarks: {
    ipca: { value: number; date: string | null } | null;
    igpm: { value: number; date: string | null } | null;
  };
  asOf: string | null;
};

const TITLE_LABELS: Record<string, string> = {
  PREFIXADO: 'Prefixado',
  'PREFIXADO COM JUROS SEMESTRAIS': 'Prefixado com juros semestrais',
  SELIC: 'Selic',
  'IPCA+': 'IPCA+',
  'IPCA+ COM JUROS SEMESTRAIS': 'IPCA+ com juros semestrais',
  'IGPM+ COM JUROS SEMESTRAIS': 'IGP-M+ com juros semestrais',
  'RENDA+ APOSENTADORIA EXTRA': 'RendA+ aposentadoria extra',
  'EDUCA+': 'Educa+',
};

const titleLabel = (value: string) => TITLE_LABELS[value] ?? value.replaceAll('_', ' ').toLowerCase();
const instrumentCode = (
  option: Pick<CatalogOption, 'titleType' | 'indexer' | 'name' | 'idCem' | 'conversionDate' | 'maturityDate'>,
) => {
  const seriesDate = option.conversionDate ?? option.maturityDate;
  const year = seriesDate?.slice(2, 4) ?? option.name.match(/20(\d{2})/)?.[1] ?? '';
  const fullYear = seriesDate?.slice(0, 4) ?? '';
  const title = option.titleType.toUpperCase();
  if (title.includes('IPCA')) return `NTNB${year}`;
  if (title.includes('SELIC')) return `LFT${fullYear}`;
  if (title.includes('PREFIXADO')) return `LTN${fullYear}`;
  if (title.includes('IGPM')) return `NTNC${year}`;
  if (title.includes('RENDA')) return `RENDA+${fullYear}`;
  if (title.includes('EDUCA')) return `EDUCA+${fullYear}`;
  return option.idCem;
};
const displayTitle = (option: Pick<CatalogOption, 'titleType' | 'name' | 'conversionDate' | 'maturityDate'>) => {
  const year =
    option.conversionDate?.slice(0, 4) ?? option.name.match(/20\d{2}/)?.[0] ?? option.maturityDate?.slice(0, 4) ?? '';
  return `Tesouro ${titleLabel(option.titleType)}${year ? ` ${year}` : ''}`;
};

const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const formatBrl = (value: number | null | undefined) =>
  value == null || !Number.isFinite(value) ? '—' : brlFormatter.format(value);
const formatPct = (value: number | null | undefined, digits = 2) =>
  value == null || !Number.isFinite(value)
    ? '—'
    : `${value.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
const formatMultiple = (value: number | null | undefined) =>
  value == null || !Number.isFinite(value)
    ? '—'
    : `${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`;
const formatQuantity = (value: number) => value.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
const formatShortDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
};

const parseNumeric = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // Accept both pt-BR ("7,33" / "10.000,50") and plain ("7.33") inputs:
  // a comma switches to pt-BR mode; otherwise dots are decimals unless
  // there are several of them (thousands separators).
  const dotCount = (trimmed.match(/\./g) ?? []).length;
  const normalized = trimmed.includes(',')
    ? trimmed.replace(/\./g, '').replace(',', '.')
    : dotCount > 1
      ? trimmed.replace(/\./g, '')
      : trimmed;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const MS_PER_DAY = 86_400_000;

const scenarioRateLabel = (indexer: string, titleType: string) => {
  const upper = `${indexer} ${titleType}`.toUpperCase();
  if (upper.includes('IPCA')) return 'Taxa real no cenário (% a.a.)';
  if (upper.includes('IGPM')) return 'Taxa real no cenário (% a.a.)';
  if (upper.includes('SELIC')) return 'Taxa Selic no cenário (% a.a.)';
  return 'Taxa de mercado no cenário (% a.a.)';
};

const scenarioRateHint = (indexer: string) => {
  if (isRealRateIndexed(indexer)) {
    return 'Taxa real (acima da inflação) que o mercado praticará no horizonte. O valor nominal também depende da hipótese de inflação.';
  }
  if (indexer.toUpperCase().includes('SELIC')) {
    return 'Taxa Selic média assumida até o horizonte; títulos Selic têm marcação a mercado pequena.';
  }
  return 'Taxa prefixada que o mercado praticará para este vencimento no horizonte.';
};

type MetricCardProps = {
  label: string;
  value: string;
  hint?: string;
  color?: string;
};

function MetricCard({ label, value, hint, color }: MetricCardProps) {
  return (
    <Paper
      variant='outlined'
      sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, height: '100%' }}
    >
      <Typography variant='caption' color='text.secondary' fontWeight={700}>
        {label}
      </Typography>
      <Typography
        component='div'
        sx={{ fontSize: '1.25rem', fontWeight: 850, letterSpacing: '-.02em', color: color ?? 'text.primary' }}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.35 }}>
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

type ChartMarker = { date: string; label: string; color: string };

function PuEvolutionChart({ series, markers }: { series: SimulationSeriesPoint[]; markers: ChartMarker[] }) {
  if (series.length < 2) return null;
  const width = 720;
  const height = 260;
  const left = 62;
  const right = 16;
  const top = 18;
  const bottom = 44;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const values = series.map((point) => point.pu);
  const xMin = Date.parse(series[0].date);
  const xMax = Date.parse(series[series.length - 1].date);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const padding = rawMin === rawMax ? Math.max(1, Math.abs(rawMin) * 0.1) : (rawMax - rawMin) * 0.1;
  const yMin = rawMin - padding;
  const yMax = rawMax + padding;
  const xPosition = (value: number) => left + ((value - xMin) / Math.max(1, xMax - xMin)) * plotWidth;
  const yPosition = (value: number) => top + ((yMax - value) / Math.max(1e-9, yMax - yMin)) * plotHeight;
  const path = (points: SimulationSeriesPoint[]) =>
    points
      .map(
        (point, index) =>
          `${index === 0 ? 'M' : 'L'} ${xPosition(Date.parse(point.date)).toFixed(1)} ${yPosition(point.pu).toFixed(1)}`,
      )
      .join(' ');
  const historyPoints = series.filter((point) => point.kind === 'history');
  const scenarioPoints = series.filter((point) => point.kind === 'scenario');
  const yTicks = Array.from({ length: 5 }, (_, index) => yMin + ((yMax - yMin) * index) / 4).reverse();
  const xTicks = Array.from({ length: 5 }, (_, index) => xMin + ((xMax - xMin) * index) / 4);

  return (
    <Box
      component='svg'
      viewBox={`0 0 ${width} ${height}`}
      role='img'
      aria-label='Evolução do preço unitário: histórico e cenário projetado'
      sx={{ display: 'block', width: '100%', height: 'auto', color: 'text.secondary' }}
    >
      {yTicks.map((tick) => (
        <g key={`y-${tick.toFixed(0)}`}>
          <line
            x1={left}
            x2={width - right}
            y1={yPosition(tick)}
            y2={yPosition(tick)}
            stroke='currentColor'
            opacity={0.12}
            strokeDasharray='3 4'
          />
          <text x={left - 8} y={yPosition(tick) + 4} textAnchor='end' fontSize='11' fill='currentColor' opacity={0.72}>
            {Math.round(tick).toLocaleString('pt-BR')}
          </text>
        </g>
      ))}
      {xTicks.map((tick) => (
        <text
          key={`x-${tick.toFixed(0)}`}
          x={xPosition(tick)}
          y={top + plotHeight + 18}
          textAnchor='middle'
          fontSize='11'
          fill='currentColor'
          opacity={0.72}
        >
          {formatIsoDate(new Date(tick)).slice(0, 7)}
        </text>
      ))}
      <line x1={left} x2={left} y1={top} y2={top + plotHeight} stroke='currentColor' opacity={0.45} />
      <line
        x1={left}
        x2={width - right}
        y1={top + plotHeight}
        y2={top + plotHeight}
        stroke='currentColor'
        opacity={0.45}
      />
      {historyPoints.length > 1 ? (
        <path d={path(historyPoints)} fill='none' stroke='hsl(var(--primary))' strokeWidth='2' strokeLinecap='round' />
      ) : null}
      {scenarioPoints.length > 1 ? (
        <path
          d={path(scenarioPoints)}
          fill='none'
          stroke='hsl(var(--primary))'
          strokeWidth='2'
          strokeDasharray='6 4'
          strokeLinecap='round'
          opacity={0.85}
        />
      ) : null}
      {markers.map((marker) => {
        const point = series.find((item) => item.date === marker.date);
        if (!point) return null;
        return (
          <g key={marker.label}>
            <circle cx={xPosition(Date.parse(point.date))} cy={yPosition(point.pu)} r='4.5' fill={marker.color}>
              <title>{`${marker.label} · ${formatShortDate(point.date)} · ${formatBrl(point.pu)}`}</title>
            </circle>
          </g>
        );
      })}
    </Box>
  );
}

export default function FixedIncomeSimulator() {
  const [options, setOptions] = useState<CatalogOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState(false);
  const [selected, setSelected] = useState<CatalogOption | null>(null);
  const [detail, setDetail] = useState<SimulationPayload | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState('');
  const [amountStr, setAmountStr] = useState('10000');
  const [horizonYears, setHorizonYears] = useState(5);
  const [scenarioRatePct, setScenarioRatePct] = useState('');
  const [inflationPct, setInflationPct] = useState('');
  const [overridePuStr, setOverridePuStr] = useState('');
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setOptionsLoading(true);
    setOptionsError(false);
    const maturityFrom = new Date().toISOString().slice(0, 10);
    fetch(`/api/renda-fixa/catalogo?maturityFrom=${maturityFrom}`, { cache: 'no-store', signal: controller.signal })
      .then((result) =>
        result.ok ? (result.json() as Promise<{ data: CatalogOption[] }>) : Promise.reject(new Error('catalog')),
      )
      .then((payload) => setOptions(payload.data))
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setOptionsError(true);
      })
      .finally(() => setOptionsLoading(false));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    const controller = new AbortController();
    setDetailLoading(true);
    setDetailError(false);
    fetch(`/api/renda-fixa/simulacao?code=${encodeURIComponent(selected.idCem)}`, {
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((result) =>
        result.ok ? (result.json() as Promise<SimulationPayload>) : Promise.reject(new Error('simulation data')),
      )
      .then((payload) => {
        setDetail(payload);
        setOverridePuStr('');
        const lastQuote = payload.quotes[payload.quotes.length - 1];
        setPurchaseDate(lastQuote?.date ?? '');
        setAmountStr('10000');
        setScenarioRatePct(String(payload.instrument.sellRateMorning ?? lastQuote?.sellRate ?? ''));
        const usesIgpm = payload.instrument.indexer.toUpperCase().includes('IGPM');
        const defaultInflation = usesIgpm ? payload.benchmarks.igpm?.value : payload.benchmarks.ipca?.value;
        // Benchmarks are monthly variations (% no mês); the hypothesis input is % a.a.
        setInflationPct(
          defaultInflation != null ? ((Math.pow(1 + defaultInflation / 100, 12) - 1) * 100).toFixed(2) : '4.5',
        );
        const ceiling = payload.instrument.conversionDate ?? payload.instrument.maturityDate;
        if (ceiling && lastQuote) {
          const yearsToCeiling = (Date.parse(ceiling) - Date.parse(lastQuote.date)) / (365 * MS_PER_DAY);
          setHorizonYears(Math.max(0.5, Math.round(Math.min(5, yearsToCeiling) * 2) / 2));
        }
      })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setDetailError(true);
      })
      .finally(() => setDetailLoading(false));
    return () => controller.abort();
  }, [selected]);

  const firstQuoteDate = detail?.quotes[0]?.date ?? null;
  const today = detail?.quotes[detail.quotes.length - 1]?.date ?? null;
  const ceilingDate = detail?.instrument.conversionDate ?? detail?.instrument.maturityDate ?? null;

  const effectivePurchaseDate = useMemo(() => {
    if (!firstQuoteDate || !today) return null;
    if (!purchaseDate) return today;
    if (purchaseDate < firstQuoteDate) return firstQuoteDate;
    if (purchaseDate > today) return today;
    return purchaseDate;
  }, [firstQuoteDate, today, purchaseDate]);

  const maxHorizonYears = useMemo(() => {
    if (!effectivePurchaseDate || !ceilingDate) return 5;
    const years = (Date.parse(ceilingDate) - Date.parse(effectivePurchaseDate)) / (365 * MS_PER_DAY);
    return Math.max(0.5, Math.min(35, Math.floor(years * 2) / 2));
  }, [effectivePurchaseDate, ceilingDate]);

  const horizonDate = useMemo(() => {
    if (!effectivePurchaseDate) return null;
    const ceiling = ceilingDate ?? formatIsoDate(addDays(parseIsoDate(effectivePurchaseDate), 1));
    const target = formatIsoDate(addDays(parseIsoDate(effectivePurchaseDate), Math.round(horizonYears * 365)));
    if (target > ceiling) return ceiling;
    return target <= effectivePurchaseDate ? formatIsoDate(addDays(parseIsoDate(effectivePurchaseDate), 1)) : target;
  }, [effectivePurchaseDate, ceilingDate, horizonYears]);

  const simulation = useMemo<{ result: SimulationResult | null }>(() => {
    if (!detail || !effectivePurchaseDate || !horizonDate) return { result: null };
    const amount = parseNumeric(amountStr);
    const scenarioRate = parseNumeric(scenarioRatePct);
    const inflation = parseNumeric(inflationPct);
    if (amount == null || amount <= 0 || scenarioRate == null) return { result: null };
    try {
      return {
        result: simulateTreasuryMarkToMarket({
          instrument: {
            idCem: detail.instrument.idCem,
            name: detail.instrument.name,
            titleType: detail.instrument.titleType,
            indexer: detail.instrument.indexer,
            cashFlowType: detail.instrument.cashFlowType,
            riskFactor: detail.instrument.riskFactor,
            maturityDate: detail.instrument.maturityDate ?? '',
            conversionDate: detail.instrument.conversionDate,
          },
          flows: detail.flows,
          quotes: detail.quotes,
          purchaseDate: effectivePurchaseDate,
          amount,
          horizonDate,
          scenarioRatePct: scenarioRate,
          inflationAssumptionPct: inflation ?? 0,
          custodyFeeRate: detail.custodyFeeRate,
          calibrationRatePct: detail.instrument.sellRateMorning,
          calibrationPu: detail.quotes[detail.quotes.length - 1]?.sellPu ?? null,
          overridePurchasePu: parseNumeric(overridePuStr),
        }),
      };
    } catch {
      return { result: null };
    }
  }, [detail, effectivePurchaseDate, horizonDate, amountStr, scenarioRatePct, inflationPct, overridePuStr]);

  const result = simulation.result;
  const showInflation = detail != null && isRealRateIndexed(detail.instrument.indexer);
  const chartMarkers: ChartMarker[] = useMemo(() => {
    if (!result || !today) return [];
    return [
      { date: result.purchase.quoteDateUsed, label: 'Compra', color: 'hsl(var(--foreground))' },
      { date: today, label: 'Hoje', color: 'hsl(var(--muted-foreground))' },
      { date: result.horizon.date, label: 'Horizonte', color: 'hsl(var(--primary))' },
    ];
  }, [result, today]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
      <Box>
        <Typography component='h1' variant='h5' sx={{ fontWeight: 850, letterSpacing: '-.02em' }}>
          Simulação de marcação a mercado
        </Typography>
        <Typography color='text.secondary' sx={{ mt: 0.5, lineHeight: 1.5, maxWidth: 760 }}>
          Escolha um título do Tesouro Direto, simule a compra em qualquer data (o sistema conhece a taxa praticada
          naquele dia) e projete quanto o título valeria se a taxa de mercado estivesse em outro nível no horizonte —
          com impostos, taxa de custódia e retorno líquido anualizado.
        </Typography>
      </Box>

      {optionsError ? (
        <Alert severity='error'>Não foi possível carregar os títulos disponíveis. Tente novamente.</Alert>
      ) : null}

      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1.5, md: 2 }}
          sx={{ '& .MuiFormControl-root': { minWidth: 0 } }}
        >
          <Autocomplete
            sx={{ flex: { md: 2 } }}
            options={options}
            value={selected}
            loading={optionsLoading}
            onChange={(_, option) => setSelected(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${instrumentCode(option)} - ${displayTitle(option)}`}
            filterOptions={(items, state) => {
              const query = state.inputValue.trim().toLowerCase();
              // The Treasury universe is small; a short cap ordered by
              // maturity hides the newest series from the default list.
              if (!query) return items;
              return items.filter((option) =>
                `${instrumentCode(option)} ${displayTitle(option)} ${option.titleType}`.toLowerCase().includes(query),
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                label='Título'
                placeholder='Buscar título ou código'
                inputProps={{ ...params.inputProps, suppressHydrationWarning: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {optionsLoading || detailLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            sx={{ flex: { md: 1 } }}
            size='small'
            label='Data de compra'
            type='date'
            value={purchaseDate}
            onChange={(event) => setPurchaseDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: firstQuoteDate ?? undefined, max: today ?? undefined, suppressHydrationWarning: true }}
            disabled={!detail}
            helperText={
              firstQuoteDate
                ? `Série disponível de ${formatShortDate(firstQuoteDate)} a ${formatShortDate(today)}`
                : undefined
            }
          />
          <TextField
            sx={{ flex: { md: 1 } }}
            size='small'
            label='Valor investido'
            value={amountStr}
            onChange={(event) => setAmountStr(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position='start'>R$</InputAdornment> }}
            disabled={!detail}
          />
        </Stack>

        {detail ? (
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant='caption' color='text.secondary' fontWeight={700}>
                  Horizonte da simulação: {formatShortDate(horizonDate)} ({horizonYears.toLocaleString('pt-BR')} anos
                  após a compra)
                </Typography>
                <Slider
                  value={Math.min(horizonYears, maxHorizonYears)}
                  min={0.5}
                  max={maxHorizonYears}
                  step={0.5}
                  marks={
                    maxHorizonYears <= 6
                      ? [0.5, 1, 2, 3, 4, 5, 6]
                          .filter((mark) => mark <= maxHorizonYears)
                          .map((mark) => ({ value: mark, label: `${mark}` }))
                      : true
                  }
                  onChange={(_, value) => setHorizonYears(value as number)}
                  valueLabelDisplay='auto'
                  valueLabelFormat={(value) => `${value} a`}
                />
              </Box>
              <TextField
                sx={{ flex: 1, minWidth: 0 }}
                size='small'
                label={scenarioRateLabel(detail.instrument.indexer, detail.instrument.titleType)}
                value={scenarioRatePct}
                onChange={(event) => setScenarioRatePct(event.target.value)}
                helperText={scenarioRateHint(detail.instrument.indexer)}
              />
              {showInflation ? (
                <TextField
                  sx={{ flex: 1, minWidth: 0 }}
                  size='small'
                  label={`Inflação assumida (${detail.instrument.indexer.toUpperCase().includes('IGPM') ? 'IGP-M' : 'IPCA'}, % a.a.)`}
                  value={inflationPct}
                  onChange={(event) => setInflationPct(event.target.value)}
                  helperText='Hipótese de inflação média até o horizonte.'
                />
              ) : null}
            </Stack>
            <FormControlLabel
              control={
                <Switch size='small' checked={advanced} onChange={(event) => setAdvanced(event.target.checked)} />
              }
              label='Parâmetros avançados'
              sx={{ alignSelf: 'flex-start' }}
            />
            <Collapse in={advanced}>
              <TextField
                size='small'
                label='PU de compra efetivo (opcional)'
                value={overridePuStr}
                onChange={(event) => setOverridePuStr(event.target.value)}
                helperText='Sobrescreve o PU da data escolhida — útil quando a compra real teve preço diferente do mercado.'
                sx={{ width: { xs: '100%', md: 320 } }}
              />
            </Collapse>
          </Stack>
        ) : null}
      </Paper>

      {detailError ? <Alert severity='error'>Não foi possível carregar os dados deste título.</Alert> : null}

      {!selected ? (
        <Paper
          variant='outlined'
          sx={{
            p: 4,
            borderStyle: 'dashed',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            textAlign: 'center',
          }}
        >
          <LineChartIcon size={22} color='hsl(var(--primary))' />
          <Typography variant='subtitle2' fontWeight={800}>
            Escolha um título para começar
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 460 }}>
            A simulação usa as cotações oficiais do Tesouro Transparente: o PU e a taxa da data de compra são os
            publicados naquele dia, e o cenário projeta o preço futuro pela taxa que você assumir.
          </Typography>
        </Paper>
      ) : null}

      {result && detail ? (
        <>
          {result.warnings.length > 0 ? (
            <Alert severity='info' sx={{ '& ul': { m: 0, pl: 2 } }}>
              <ul>
                {result.warnings.map((warning) => (
                  <li key={warning}>
                    <Typography variant='body2'>{warning}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          ) : null}

          <Box>
            <Stack direction='row' alignItems='baseline' spacing={1} flexWrap='wrap' useFlexGap>
              <Typography component='h2' variant='h6' sx={{ fontWeight: 850 }}>
                Cenário: {displayTitle(selected ?? detail.instrument)} comprado em{' '}
                {formatShortDate(result.purchase.date)}
              </Typography>
              <Chip size='small' label={`PU de compra ${formatBrl(result.purchase.pu)}`} />
              {result.purchase.rate != null ? (
                <Chip size='small' label={`Taxa ${formatPct(result.purchase.rate)} a.a.`} />
              ) : null}
              <Chip size='small' label={`${formatQuantity(result.purchase.quantity)} título(s)`} />
            </Stack>

            <Box
              sx={{
                mt: 1.5,
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              <MetricCard
                label={`PU simulado em ${formatShortDate(result.horizon.date)}`}
                value={formatBrl(result.horizon.pu)}
                hint={`Valor bruto de ${formatBrl(result.horizon.grossValue)}`}
              />
              <MetricCard
                label='Impostos na venda'
                value={formatBrl(result.horizon.irDue + result.horizon.iofDue)}
                color={result.horizon.irDue + result.horizon.iofDue > 0 ? 'hsl(var(--destructive))' : undefined}
                hint={
                  result.horizon.irDue + result.horizon.iofDue > 0
                    ? `IR ${formatPct(result.horizon.irRate * 100, 1)}${result.horizon.iofDue > 0 ? ` + IOF ${formatPct(result.horizon.iofRate * 100, 1)}` : ''} sobre o ganho`
                    : 'Sem ganho na venda — IR e IOF não incidem'
                }
              />
              <MetricCard
                label='Custódia (estimada)'
                value={formatBrl(result.horizon.custodyEstimate)}
                hint={`${formatPct(detail.custodyFeeRate * 100, 2)} a.a. pro-rata sobre o valor`}
              />
              <MetricCard
                label='Valor líquido se vender'
                value={formatBrl(result.horizon.netValue)}
                color='hsl(var(--primary))'
                hint={`Líquido de impostos e custódia${result.horizon.couponsNet > 0 ? ` · cupons líquidos ${formatBrl(result.horizon.couponsNet)}` : ''}`}
              />
              <MetricCard
                label='Retorno líquido'
                value={formatMultiple(result.horizon.netMultiple)}
                hint={`${formatPct(result.horizon.netAnnualReturn == null ? null : result.horizon.netAnnualReturn * 100)} a.a. líquidos`}
                color='hsl(var(--primary))'
              />
            </Box>
          </Box>

          <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
            <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
              <LineChartIcon size={16} color='hsl(var(--primary))' />
              <Typography variant='subtitle2' fontWeight={800}>
                Preço unitário: histórico e trajetória projetada
              </Typography>
            </Stack>
            <PuEvolutionChart series={result.series} markers={chartMarkers} />
            <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
              <Typography variant='caption' color='text.secondary'>
                — histórico publicado (compra → hoje)
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                – – trajetória sob o cenário (hoje → vencimento)
              </Typography>
            </Stack>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.25,
            }}
          >
            <Paper variant='outlined' sx={{ p: 2, borderColor: 'hsl(var(--primary) / .5)' }}>
              <Typography variant='caption' color='text.secondary' fontWeight={700}>
                VENDER EM {formatShortDate(result.horizon.date).toUpperCase()}
              </Typography>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: 'hsl(var(--primary))' }}>
                {formatBrl(result.horizon.netValue)}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {formatMultiple(result.horizon.netMultiple)} do valor investido ·{' '}
                {formatPct(result.horizon.netAnnualReturn == null ? null : result.horizon.netAnnualReturn * 100)} a.a.
                líquidos
              </Typography>
            </Paper>
            <Paper variant='outlined' sx={{ p: 2 }}>
              <Typography variant='caption' color='text.secondary' fontWeight={700}>
                SEGURAR ATÉ O VENCIMENTO ({formatShortDate(result.maturity.date).toUpperCase()})
              </Typography>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 900 }}>
                {formatBrl(result.maturity.netValue)}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {formatMultiple(result.maturity.netMultiple)} do valor investido ·{' '}
                {formatPct(result.maturity.netAnnualReturn == null ? null : result.maturity.netAnnualReturn * 100)} a.a.
                líquidos
              </Typography>
            </Paper>
          </Box>

          <Divider />
        </>
      ) : null}

      {detail ? (
        <Stack spacing={0.5}>
          <Typography variant='caption' color='text.secondary'>
            Fonte: Tesouro Transparente (cotações de oferta). Cenários são hipóteses suas, não recomendação de
            investimento. A isenção da taxa de custódia para Tesouro Selic (até R$ 10 mil por CPF) não é aplicada — a
            estimativa considera o valor cheio da taxa.
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            IR regressivo sobre o ganho (22,5% a 15% conforme o prazo; 15% em cada cupom pago) e IOF nas vendas com
            menos de 30 dias. Em títulos IPCA+/IGP-M+ o preço é projetado em termos reais e convertido pela inflação
            assumida. Valores simulados são estimativas para estudo.
          </Typography>
        </Stack>
      ) : null}
    </Box>
  );
}
